import React, { Suspense } from 'react'
import { useCookies } from 'react-cookie'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import { useTheme } from '@mui/material'
import { AxiosInterceptor } from 'api'
import { BaseLayout } from 'components'
import { Loading } from 'components/ui'
import { ConsentConstants } from 'pages/constants'
import Language from 'pages/dashboard/language'
import ErrorPage from 'pages/error/error-page'
import PasskeyAuthenticationError from 'pages/error/passkeys'
import RegionError from 'pages/error/region/region-error'
import TemporaryIdExpiredError from 'pages/error/temporary-id'
import Notices from 'pages/notices'
import Policy from 'pages/policies'
import TabsLayout from 'pages/policies/privacy-notices/kr/layout/tabs-layout'
import { Services } from 'pages/policies/privacy-notices/kr/services/services'
import { ServicesList } from 'pages/policies/privacy-notices/kr/services/services-list'
import PrivacyNotices from 'pages/policies/privacy-notices/privacy-notices'
import { PrivacyNoticesAndPolicies } from 'pages/policies/privacy-notices-and-policies/privacy-notices-and-policies'
import SpecialTerms from 'pages/policies/specials/special-terms'
import TermsConditions from 'pages/policies/terms-conditions/terms-conditions'
import TermsConditionsService from 'pages/policies/terms-conditions/terms-conditions-service'
import TermsConditionsServiceList from 'pages/policies/terms-conditions/terms-conditions-service-list'
import WeChatPopup from 'pages/popup/wechat'
import { Redirect } from 'router/redirect'
import { generateRandomString } from 'utils/common'

import { RouterConstants } from './constants'
import PageComponent from './page-component'
import DashboardRoutes from './routes/dashboard-routes'
import GuideRoutes from './routes/guide-routes'
import LegacyRoutes from './routes/legacy-routes'
import Operators from '../pages/policies/privacy-notices/operators/operators'
import PrivacyNoticesList from '../pages/policies/privacy-notices/priacy-notices-list'
import ThirdPatry from '../pages/policies/privacy-notices/third-party/third-patry'
import PrivacyPolicies from '../pages/policies/privacy-policies/privacy-policies'
import { TermsConditionsList } from '../pages/policies/terms-conditions/terms-conditions-list'
import TermsConditionsServiceHistory from '../pages/policies/terms-conditions/terms-conditions-service-history'

const AdaptiveAuthenticationEmail = React.lazy(() => import('pages/adaptive-authentication/email'))
const ComingSoon = React.lazy(() => import('pages/error/experiment'))
const Index = React.lazy(() => import('pages'))

export function Router() {
  const theme = useTheme()

  const [cookies, setCookie] = useCookies()

  if (cookies.sa_trace === undefined || cookies.sa_trace === '') {
    setCookie('sa_trace', generateRandomString(32), {
      domain: `${process.env.REACT_APP_COOKIE_DOMAIN}`,
      path: '/'
    })
  }

  return (
    <BrowserRouter>
      {<AxiosInterceptor />}
      <Suspense fallback={<Loading fullScreen />}>
        <Routes>
          <Route element={<BaseLayout sx={{ background: theme.palette.background.paper }} />}>
            <Route path='/' element={<PageComponent component={<Index />} name='Index' />} />
            <Route path='/notices' element={<PageComponent component={<Notices />} name='Notices' />} />
            <Route path='/notices/:noticeId' element={<PageComponent component={<Notices />} name='Notices' />} />
            <Route path='/notices/legacies' element={<PageComponent component={<Notices />} name='Notices' />} />
            <Route path='/notices/legacies/:noticeId' element={<PageComponent component={<Notices />} name='Notices' />} />

            <Route
              path='/policies/terms-conditions/latest'
              element={<PageComponent component={<TermsConditions />} name='TermsConditions' />}
            />
            <Route
              path='/policies/terms-conditions'
              element={<PageComponent component={<TermsConditionsList />} name='TermsConditionsList' />}
            />
            <Route
              path='/policies/terms-conditions/:versionId'
              element={<PageComponent component={<TermsConditionsList />} name='TermsConditionsList' />}
            />
            <Route
              path='/policies/terms-conditions/services'
              element={<PageComponent component={<TermsConditionsServiceList />} name='TermsConditionsServiceList' />}
            />
            <Route
              path='/policies/terms-conditions/services/:appId/latest'
              element={<PageComponent component={<TermsConditionsService />} name='TermsConditionsService' />}
            />
            <Route
              path='/policies/terms-conditions/services/:appId'
              element={<PageComponent component={<TermsConditionsServiceHistory />} name='TermsConditionsServiceHistory' />}
            />
            <Route
              path='/policies/privacy-notices/operators/:consentId'
              element={<PageComponent component={<Operators />} name='PrivacyNoticesOperator' />}
            />
            <Route
              path='/policies/privacy-notices/operators'
              element={<PageComponent component={<Operators />} name='PrivacyNoticesOperator' />}
            />

            <Route element={<TabsLayout />}>
              <Route
                path={'/policies/privacy-notices/services/legacies/:appId/:versionId'}
                element={<PageComponent component={<Services />} name='Services' />}
              />
              <Route
                path={'/policies/privacy-notices/services/legacies/:appId'}
                element={<PageComponent component={<Services />} name='Services' />}
              />
              <Route
                path={'/policies/privacy-notices/services/:appId/:versionId'}
                element={<PageComponent component={<Services />} name='Services' />}
              />
              <Route
                path={'/policies/privacy-notices/services/:appId'}
                element={<PageComponent component={<Services />} name='Services' />}
              />
              <Route
                path={RouterConstants.PRIVACY_NOTICES_SERVICES_APP_INFO}
                element={
                  <PageComponent
                    component={<PrivacyNoticesAndPolicies consentCode={ConsentConstants.PRIVACY_NOTICE} />}
                    name='Services'
                  />
                }
              />
              <Route
                path={'/policies/privacy-notices/services'}
                element={<PageComponent component={<ServicesList />} name='ServicesList' />}
              />
              <Route
                path={'/policies/privacy-notices/legacies'}
                element={<PageComponent component={<PrivacyNoticesList />} name='PrivacyNoticesListLegacies' />}
              />
              <Route
                path={'/policies/privacy-notices/legacies/:versionId'}
                element={<PageComponent component={<PrivacyNoticesList />} name='PrivacyNoticesListLegacies' />}
              />
              <Route
                path='/policies/privacy-notices/latest'
                element={<PageComponent component={<PrivacyNotices />} name='PrivacyNotices' />}
              />
              <Route
                path={'/policies/privacy-notices/:versionId'}
                element={<PageComponent component={<PrivacyNoticesList />} name='PrivacyNoticesList' />}
              />
              <Route
                path={'/policies/privacy-notices'}
                element={<PageComponent component={<PrivacyNoticesList />} name='PrivacyNoticesList' />}
              />
            </Route>
            <Route
              path='/policies/privacy-policies'
              element={<PageComponent component={<PrivacyPolicies />} name='PrivacyPolicies' />}
            />
            <Route
              path={RouterConstants.PRIVACY_POLICIES_SERVICES_APP_INFO}
              element={
                <PageComponent
                  component={<PrivacyNoticesAndPolicies consentCode={ConsentConstants.PRIVACY_POLICY} />}
                  name='Policies'
                />
              }
            />
            <Route
              path='/policies/specials/:filename'
              element={<PageComponent component={<SpecialTerms />} name='SpecialTerms' />}
            />
            <Route path='/policies/:consentType/' element={<PageComponent component={<Policy />} name='Policy' />} />

            <Route path='/policy/:consentType/' element={<Redirect to='/policies/:consentType' />} />
            <Route path='*' element={<PageComponent component={<ErrorPage />} name='ErrorPage' />} />
          </Route>

          <Route element={<BaseLayout />}>
            <Route
              path='/policies/privacy-notices/third-party'
              element={<PageComponent component={<ThirdPatry />} name='PrivacyNoticesThirdParty' />}
            />
            <Route path='/language' element={<PageComponent component={<Language />} name='Language' />} />
            <Route path='/error/500' element={<Navigate to='/error' replace />} />
            <Route path='/error/:errorCode' element={<PageComponent component={<ErrorPage />} name='ErrorPage' />} />
            <Route path='/error/coming-soon' element={<PageComponent component={<ComingSoon />} name='ComingSoon' />} />
            <Route path='/error/region' element={<PageComponent component={<RegionError />} name='RegionRedirect' />} />
            <Route path='/error' element={<PageComponent component={<ErrorPage />} name='ErrorPage' />} />
            <Route
              path='/error/passkey'
              element={<PageComponent component={<PasskeyAuthenticationError />} name='PasskeyAuthenticationError' />}
            />
            <Route
              path='/adaptive-authentication/email'
              element={<PageComponent component={<AdaptiveAuthenticationEmail />} name='AdaptiveAuthenticationEmail' />}
            />
            <Route
              path='/error/temporary-id-expired'
              element={<PageComponent component={<TemporaryIdExpiredError />} name='TemporaryIdExpiredError' />}
            />
          </Route>

          <Route path='/dashboard/*' element={<DashboardRoutes />} />
          <Route path='/guide/*' element={<GuideRoutes />} />
          <Route path='/popup/wechat' element={<PageComponent component={<WeChatPopup />} name='WeChatPopup' />} />
          <Route path='/membership/*' element={<LegacyRoutes />} />
          <Route path='/membershipintro/*' element={<LegacyRoutes />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
